// Implements: F-VER-016, F-UIX-013
/**
 * The drawn diagram's "Descargar (ZIP)" (mockup v3 P6, `data-act="export" data-v="diagram"`): the
 * version's `diagram.zip` (the journey, and the walkthrough when the version stores it), through the
 * client, exactly as "Más acciones > Diagrama (ZIP)" downloads it (`useExportMockupVersion` in
 * `mockups`, which this module may not import): the shared `downloadVersionFile` and `saveBlob`, so a
 * 403 or a 404 is a toast with the server's sentence, never an error body saved as the file.
 *
 * A `GET`, so nothing is charged and the client's retry of a 5xx applies. While one download is in
 * flight the button is off, so a double press asks once.
 */

import { useCallback, useRef, useState } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { defaultDownloadName, downloadVersionFile } from "../../../shared/api/versionDownloads";
import { useTranslation } from "../../../shared/i18n";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { saveBlob } from "../../../shared/ui/saveBlob";

export interface DiagramDownload {
  readonly download: () => void;
  readonly pending: boolean;
}

export function useDiagramDownload(mockupId: string, versionId: string, versionNumber: number): DiagramDownload {
  const { t } = useTranslation();
  const { push } = useToast();
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  const download = useCallback((): void => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    downloadVersionFile(mockupId, versionId, "diagram")
      .then((file) => {
        saveBlob(file.blob, file.filename ?? defaultDownloadName("diagram", versionNumber));
      })
      .catch((cause: unknown) => {
        push({ tone: "danger", message: userMessage(asApiError(cause), t("diagram.drawn.downloadFailed")) });
      })
      .finally(() => {
        inFlight.current = false;
        setPending(false);
      });
  }, [mockupId, versionId, versionNumber, push, t]);

  return { download, pending };
}
